import {ProseMirror, ProseMirrorDoc, reactKeys} from '@handlewithcare/react-prosemirror'
import {clsx} from 'clsx'
import {baseKeymap} from 'prosemirror-commands'
import {history, redo, undo} from 'prosemirror-history'
import {keymap} from 'prosemirror-keymap'
import {EditorState} from 'prosemirror-state'
import {useState} from 'react'

import styles from './HybridMarkdownEditor.module.css'
import {decorateActiveHybridBlocksPlugin} from './prosemirror/plugins/decorate-active-hybrid-blocks'
import {decorateHybridMarkdownPlugin} from './prosemirror/plugins/decorate-inline-markdown'
import {schema} from './prosemirror/schema/hybrid-markdown-schema'
import {parseMarkdownToProseMirror, serializeProseMirrorDocToMarkdown} from './prosemirror/schema/utils'
import {type MarkdownViewMode, ViewSwitch} from './ViewSwitch'

const plugins = [
  history(),
  reactKeys(),
  keymap({'Mod-z': undo, 'Mod-y': redo}),
  keymap(baseKeymap),
  decorateHybridMarkdownPlugin(),
  decorateActiveHybridBlocksPlugin(),
]

export interface HybridMarkdownEditorProps {
  /** This is only the initial value. Changes to this value after mount are ignored. */
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  onChangeViewMode?: (newViewMode: MarkdownViewMode) => void
}

export function HybridMarkdownEditor({value, onChange, disabled, onChangeViewMode}: HybridMarkdownEditorProps) {
  const [editorState, setEditorState] = useState(() =>
    EditorState.create({
      doc: parseMarkdownToProseMirror(value),
      schema,
      plugins,
    }),
  )

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.viewSwitch}>
          <ViewSwitch selectedView="hybrid" onViewSelect={onChangeViewMode} />
        </div>
        <div className={styles.formattingTools} />
      </div>

      <ProseMirror
        state={editorState}
        dispatchTransaction={transaction => {
          setEditorState(s => s.apply(transaction))
          onChange(serializeProseMirrorDocToMarkdown(transaction.doc))
        }}
        editable={() => !disabled}
        className={clsx(styles.content, 'markdown-body')}
      >
        {/* TODO: Allow consumer to set (visible?) label */}
        <ProseMirrorDoc aria-label="Markdown input" role="textbox" aria-multiline aria-readonly={disabled} />
      </ProseMirror>
    </div>
  )
}

try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}